import React from "react";
import styled from "styled-components";
import { useSelector } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExclamationTriangle,
  faBaby,
  faWheelchairMove,
  faCheck,
} from "@fortawesome/free-solid-svg-icons";
import {
  CalendarSvgIcon,
  PhoneSvgIcon,
  TableSvgIcon,
  UsersSvgIcon,
  UserSvgIcon,
  ClockSvgIcon,
  ZoneSvgIcon,
  NoPhoneSvgIcon,
  NoPhoneTwoLinesSvgIcon,
} from "../../icons/SvgIcons";
import {
  getReservationStatusAbreviationTranslation,
  getReservationStatusColors,
} from "../ReservationStatus";
import { DefaultDateUpper } from "../DefaultImports";
import Tooltip from "../ToolTipWrapper";

// Styled Components (compact)
const Card = styled.div`
  background: #fff;
  font-size: 15px;
`;

const Box = styled.div`
  display: flex;
  flex-direction: column; /* rows stack vertically */
  gap: 3px;
  padding: 3px;
`;

const BoxRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const BoxItems = styled.div`
  display: flex;
  align-items: center;
  margin: 2px 0px;
  color: #99b0ad;
`;

const ChangeHighlight = ({
  previous,
  current,
  isTablesUsed = false,
  isZones = false,
  isTime = false,
  isWaitingList = false,
  forceChanged,
}) => {
  const calculatedChanged =
    previous !== undefined && String(previous) !== String(current);

  const hasChanged =
    typeof forceChanged === "boolean" ? forceChanged : calculatedChanged;
  const isPreviousUndefined =
    previous === undefined || previous === null || previous === "";

  const isCurrentUndefined =
    current === undefined || current === null || current === "";

  const displayPrevious =
    isTablesUsed && isPreviousUndefined
      ? "Sem mesas"
      : isZones && isPreviousUndefined
        ? "Sala não definida"
        : previous;

  const displayCurrent =
    isTablesUsed && isCurrentUndefined
      ? "Sem mesas"
      : isZones && isCurrentUndefined
        ? "Sem Sala"
        : isTime && isWaitingList
          ? "Hora registada " + current
          : current;

  return (
    <span
      style={{
        backgroundColor: hasChanged ? "#e6ffe6" : "transparent",
        padding: "1px 4px",
        borderRadius: "4px",
        color: hasChanged ? "green" : "#99b0ad",
        fontWeight: hasChanged ? "500" : "normal",
      }}
    >
      {hasChanged && <span style={{ color: "red" }}>{displayPrevious} → </span>}
      {displayCurrent}
    </span>
  );
};

const StatusHighlight = ({ previous, current }) => {
  const changed = previous !== undefined && previous !== current;
  const prevText = getReservationStatusAbreviationTranslation(previous);
  const currText = getReservationStatusAbreviationTranslation(current);
  const currColor = getReservationStatusColors(current);

  return (
    <>
      {changed && (
        <span style={{ color: "red", marginRight: "5px" }}>{prevText} →</span>
      )}
      <span
        style={{
          color: currColor,
          fontWeight: changed ? "500" : "normal",
        }}
      >
        {currText}
      </span>
    </>
  );
};

const ChangeDateHighlight = ({ previous, current }) => {
  const hasChanged =
    previous !== undefined && String(previous) !== String(current);

  const isPreviousEmpty =
    previous === undefined || previous === null || previous === "";
  const isCurrentEmpty =
    current === undefined || current === null || current === "";

  const displayPrevious = isPreviousEmpty ? "Data não definida" : previous;
  const displayCurrent = isCurrentEmpty ? "Data não definida" : current;

  return (
    <span
      style={{
        backgroundColor: hasChanged ? "#e6ffe6" : "transparent",
        padding: "1px 4px",
        borderRadius: "4px",
        color: hasChanged ? "green" : "#99b0ad",
        fontWeight: hasChanged ? "500" : "normal",
      }}
    >
      {hasChanged && (
        <span style={{ color: "red" }}>
          <DefaultDateUpper date={displayPrevious} /> →{" "}
        </span>
      )}
      <DefaultDateUpper date={displayCurrent} />
    </span>
  );
};

function ReservationItems({
  reservation,
  all_tables,
  previousReservationFromHistory,
  isReservationHistory = false,
  isReservationFromRestaurantScreen,
}) {
  const reservationZone = useSelector((state) => state.reservationZone);
  const { zones } = reservationZone;

  const tablesConfigList = useSelector(
    (state) => state.connectTablesConfig.connectTablesConfig,
  );

  if (
    !reservation ||
    typeof reservation !== "object" ||
    Object.keys(reservation).length === 0
  )
    return null;

  const previousReservation = isReservationHistory
    ? previousReservationFromHistory
    : reservation.history?.[1];

  const getPrev = (key) => previousReservation?.[key];
  const getCurr = (key) =>
    key === "table_place_preference" && !isReservationHistory && reservation
      ? reservation.table_place_preference?.id
      : reservation?.[key];

  const hasBabyChairVisibleValue =
    Number(getCurr("baby_chair") || 0) !== 0 ||
    Number(getPrev("baby_chair") || 0) !== 0;

  const hasAccessibilityVisibleValue =
    getCurr("accessability") === "wheel_chair" ||
    getPrev("accessability") === "wheel_chair";

  const isCancelledStatus = [
    "Cancelled",
    "cancelled",
    "cancelled_customer",
    "cancelled_restaurant",
  ].includes(reservation.status);

  // ----------------------------
  // ✅ Shared "tables summary" logic (count + chairs)
  // ----------------------------
  const calcConfigSum = (numOfTables, configList = []) => {
    if (!Array.isArray(configList) || numOfTables < 2) return 0;

    const chairsByTableCount = configList.reduce((acc, item) => {
      const tableCount = Number(item?.number_of_tables);
      const chairs = Number(item?.number_of_chairs) || 0;

      if (!Number.isNaN(tableCount)) {
        acc[tableCount] = chairs;
      }

      return acc;
    }, {});

    let configSum = 0;

    if (numOfTables >= 2) configSum += chairsByTableCount[2] || 0;
    if (numOfTables >= 3) configSum += chairsByTableCount[3] || 0;
    if (numOfTables >= 4) configSum += chairsByTableCount[4] || 0;

    for (let i = 5; i <= numOfTables; i++) {
      configSum += chairsByTableCount[((i + 1) % 2) + 1] || 0;
    }

    return configSum;
  };

  const parseTableIdsFromString = (tablesString) => {
    if (!tablesString) return [];
    return tablesString
      .split(",")
      .map((n) => parseInt(n, 10))
      .filter((n) => !Number.isNaN(n))
      .sort((a, b) => a - b);
  };

  const getTablesSummaryFromIds = (tableIds, allTables, configList) => {
    if (
      !Array.isArray(allTables) ||
      !Array.isArray(tableIds) ||
      tableIds.length === 0
    ) {
      return {
        tableCount: 0,
        baseSeats: 0,
        configSum: 0,
        totalChairs: 0,
        label: "—",
      };
    }

    const filteredTables = allTables.filter((t) => tableIds.includes(t.id));

    const baseSeats = filteredTables.reduce(
      (acc, t) => acc + (Number.parseInt(t.number_of_seats, 10) || 0),
      0,
    );

    const tableCount = filteredTables.length;
    const configSum = calcConfigSum(tableCount, configList);
    const totalChairs = baseSeats + configSum;

    return {
      tableCount,
      baseSeats,
      configSum,
      totalChairs,
      label: `${tableCount} Mesas / ${totalChairs} Cadeiras`,
    };
  };

  const parseTablesSummary = (tablesString) => {
    if (!tablesString || !all_tables) return "";
    const ids = parseTableIdsFromString(tablesString);
    return getTablesSummaryFromIds(ids, all_tables, tablesConfigList).label;
  };
  // ----------------------------
  const getPhoneConfirmationText = (value, phoneNumber) => {
    if (
      !phoneNumber ||
      phoneNumber.trim() === "" ||
      phoneNumber.trim().length <= 3
    ) {
      return (
        <Tooltip text="Sem número de telefone">
          <NoPhoneTwoLinesSvgIcon
            width={18}
            height={16}
            strokeColor="#99B0AD"
          />
        </Tooltip>
      );
    }

    if (value === true) {
      return (
        <Tooltip text="Telefone confirmado">
          <PhoneSvgIcon width={18} height={16} strokeColor="#99B0AD" />
        </Tooltip>
      );
    }

    return (
      <Tooltip text="Telefone não confirmado">
        <NoPhoneSvgIcon width={18} height={16} strokeColor="#99B0AD" />
      </Tooltip>
    );
  };

  function formatPhoneNumber(phone, countryCode) {
    if (!phone || !countryCode) return phone;

    const cleanCode = countryCode.replace(/\D/g, "");
    const codeLength = cleanCode.length;

    if (phone.startsWith(cleanCode)) {
      return `+${cleanCode} ${phone.slice(codeLength)}`;
    }

    return `+${cleanCode} ${phone}`;
  }

  return (
    <Card>
      <Box>
        {/* Nome */}
        {isReservationHistory && (
          <BoxItems>
            <UserSvgIcon
              width={20}
              height={18}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              previous={getPrev("full_name")}
              current={getCurr("full_name")}
            />
          </BoxItems>
        )}

        {/* Telemóvel e Confirmação */}
        <BoxRow>
          {isReservationHistory && (
            <BoxItems>
              <ChangeHighlight
                forceChanged={
                  String(getPrev("is_phone_confirmed")) !==
                    String(getCurr("is_phone_confirmed")) ||
                  String(getPrev("phone_number") || "") !==
                    String(getCurr("phone_number") || "")
                }
                previous={getPhoneConfirmationText(
                  getPrev("is_phone_confirmed"),
                  getPrev("phone_number"),
                )}
                current={getPhoneConfirmationText(
                  getCurr("is_phone_confirmed"),
                  getCurr("phone_number"),
                )}
              />

              <ChangeHighlight
                previous={formatPhoneNumber(
                  getPrev("phone_number"),
                  previousReservationFromHistory?.country_code,
                )}
                current={formatPhoneNumber(
                  getCurr("phone_number"),
                  reservation?.country_code,
                )}
              />
            </BoxItems>
          )}

          {/* Data */}
          {!isReservationFromRestaurantScreen && (
            <BoxItems>
              <CalendarSvgIcon
                width={20}
                height={18}
                strokeColor="#99B0AD"
                style={{ marginRight: "4px" }}
              />
              <ChangeDateHighlight
                previous={getPrev("date")}
                current={getCurr("date")}
              />
            </BoxItems>
          )}
        </BoxRow>

        <BoxRow>
          {/* Hora */}
          <BoxItems>
            <ClockSvgIcon
              width={22}
              height={20}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              isTime={true}
              isWaitingList={reservation.waiting_list_number != null}
              previous={getPrev("time")}
              current={getCurr("time")}
            />
          </BoxItems>

          {/* Nº de Pessoas */}
          <BoxItems>
            <UsersSvgIcon
              width={24}
              height={22}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              previous={getPrev("number_of_people")}
              current={getCurr("number_of_people")}
            />
          </BoxItems>

          {/* Cadeira de bebé */}
          {hasBabyChairVisibleValue && (
            <BoxItems>
              <FontAwesomeIcon
                icon={faBaby}
                size="xl"
                style={{ color: "#99B0AD", marginRight: "4px" }}
              />
              <ChangeHighlight
                previous={getPrev("baby_chair")}
                current={getCurr("baby_chair")}
              />
            </BoxItems>
          )}
        </BoxRow>

        <BoxRow>
          {/* Zona */}
          <BoxItems>
            <ZoneSvgIcon
              width={22}
              height={20}
              strokeColor="#99B0AD"
              style={{ marginRight: "4px" }}
            />
            <ChangeHighlight
              previous={
                zones?.zones_with_id?.[getPrev("table_place_preference")]
              }
              current={
                zones?.zones_with_id?.[getCurr("table_place_preference")]
              }
              isZones={true}
            />
          </BoxItems>
        </BoxRow>

        <BoxRow>
          {/* Mesas usadas */}
          {(getCurr("tablesused") || getPrev("tablesused")) &&
            all_tables &&
            !isCancelledStatus && (
              <BoxItems>
                <TableSvgIcon
                  width={24}
                  height={22}
                  strokeColor="#99B0AD"
                  style={{ marginRight: "4px" }}
                />
                <ChangeHighlight
                  previous={parseTablesSummary(getPrev("tablesused"))}
                  current={parseTablesSummary(getCurr("tablesused"))}
                  isTablesUsed={true}
                />
              </BoxItems>
            )}

          {/* Erro: mesas insuficientes */}
          {reservation.not_enough_seats &&
            reservation.waiting_list_number === null && (
              <BoxItems
                style={{
                  backgroundColor: "#ffe6e6",
                  borderRadius: "4px",
                  padding: "4px 6px",
                }}
              >
                <FontAwesomeIcon
                  icon={faExclamationTriangle}
                  style={{
                    color: "#d15823",
                    fontSize: ".8rem",
                    marginRight: "6px",
                  }}
                />
                <span style={{ color: "#d15823", fontSize: ".8rem" }}>
                  Mesas insuficientes
                </span>
              </BoxItems>
            )}
        </BoxRow>

        {/* Estado */}
        {isReservationHistory && (
          <BoxItems>
            <FontAwesomeIcon
              icon={faCheck}
              size="xl"
              style={{ color: "#99B0AD", marginRight: "4px" }}
            />
            <StatusHighlight
              previous={getPrev("status")}
              current={getCurr("status")}
            />
          </BoxItems>
        )}
      </Box>

      <BoxRow>
        {/* Acessibilidade */}
        {hasAccessibilityVisibleValue && (
          <BoxItems>
            <FontAwesomeIcon
              icon={faWheelchairMove}
              size="xl"
              style={{ color: "#99B0AD", marginLeft: "4px" }}
            />
            <ChangeHighlight
              previous={
                getPrev("accessability") === "wheel_chair"
                  ? "Mob. Red."
                  : "Nenhuma"
              }
              current={
                getCurr("accessability") === "wheel_chair"
                  ? "Mob. Red."
                  : "Nenhuma"
              }
            />
          </BoxItems>
        )}
      </BoxRow>
    </Card>
  );
}

export default ReservationItems;
